import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useEffect, useState } from "react";

interface Props {
  label?: string;
  to?: string;
  /** Hide until user scrolls this many pixels past the top */
  showAfter?: number;
}

/**
 * Sticky bottom CTA shown only on mobile (< md). Appears after the hero
 * scrolls out so it doesn't compete with the in-hero CTA, and disappears
 * near the bottom of the page (where another CTA section usually lives).
 */
const MobileStickyCTA = ({ label = "Get Started Free", to = "/auth", showAfter = 420 }: Props) => {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const onScroll = () => {
      const y = window.scrollY;
      const nearBottom = window.innerHeight + y >= document.documentElement.scrollHeight - 600;
      setVisible(y > showAfter && !nearBottom);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [showAfter]);

  return (
    <div
      className={`md:hidden fixed inset-x-0 bottom-0 z-40 px-3 pb-[max(env(safe-area-inset-bottom),0.75rem)] pt-3 pointer-events-none transition-all duration-300 ${
        visible ? "translate-y-0 opacity-100" : "translate-y-full opacity-0"
      }`}
      aria-hidden={!visible}
    >
      <div className="pointer-events-auto rounded-full bg-white/95 backdrop-blur-xl shadow-large border border-border p-1.5 flex items-center gap-2">
        <span className="text-xs font-medium text-muted-foreground pl-3 pr-1 whitespace-nowrap">
          Start free
        </span>
        <Link to={to} className="flex-1">
          <Button variant="gradient" size="sm" className="w-full rounded-full h-10 text-sm font-semibold">
            {label}
          </Button>
        </Link>
      </div>
    </div>
  );
};

export default MobileStickyCTA;